import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import usePageTitle from "@/hooks/usePageTitle";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import { showSnackbar } from "@/lib/snackbarUtils";
import TabLayout from "../_TabLayout";
import { shouldSwitchConfigurationToLocalBackend } from "../localBackend";
import { useConfigurationAccess } from "../access";
import AssignCareerConsultantDialog from "../_components/AssignCareerConsultantDialog";
import ConfigurationLoadError from "../_components/ConfigurationLoadError";
import ConfirmCoverageChangeDialog from "../_components/ConfirmCoverageChangeDialog";
import CoverageGroupTabs, { useCoverageGroupBy } from "../_components/CoverageGroupTabs";
import GroupedCoverageTable from "../_components/GroupedCoverageTable";
import {
  CONFIGURATION_LIST_FIRST,
  filterCoverageGroups,
  groupCareerAssignmentsByConsultant,
  groupCareerAssignmentsBySection,
  isCoverageListTruncated
} from "../_components/groupCoverage";
import { CONFIGURATION_SECTIONS_QUERY, SECTION_CONSULTANT_ASSIGNMENTS_QUERY } from "../queries";
import {
  DELETE_SECTION_CONSULTANT_ASSIGNMENT_MUTATION,
  UPSERT_SECTION_CONSULTANT_ASSIGNMENT_MUTATION
} from "../mutations";
import type { CoverageGroupMember, CoverageGroupRow, CoverageStaff } from "../types";

function mutationErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function CareerConsultantsTabPage() {
  usePageTitle("/configuration/career-consultants", "Member 360 Configuration | Career Consultants");
  const { loaded, canView } = useConfigurationAccess();
  const { Menu, setMenu } = useTableRowMenu();
  const { setError } = useErrorBanner();
  const groupBy = useCoverageGroupBy();
  const [assignTarget, setAssignTarget] = useState<{
    sectionId?: string;
    sectionName?: string;
    consultant?: CoverageStaff | null;
  } | null>(null);
  const [assignmentToRemove, setAssignmentToRemove] = useState<CoverageGroupMember | null>(null);

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search",
    debounceMs: 300
  });

  const skip = !loaded || !canView || shouldSwitchConfigurationToLocalBackend();
  const {
    data: assignmentsData,
    loading: assignmentsLoading,
    error: assignmentsError,
    refetch: refetchAssignments
  } = useQuery(SECTION_CONSULTANT_ASSIGNMENTS_QUERY, {
    skip,
    variables: {
      search: undefined,
      isActive: true,
      first: CONFIGURATION_LIST_FIRST
    }
  });
  const {
    data: sectionsData,
    loading: sectionsLoading,
    error: sectionsError,
    refetch: refetchSections
  } = useQuery(CONFIGURATION_SECTIONS_QUERY, {
    skip,
    variables: {
      input: { search: undefined },
      first: CONFIGURATION_LIST_FIRST
    }
  });

  const [upsertAssignment, { loading: assigning }] = useMutation(UPSERT_SECTION_CONSULTANT_ASSIGNMENT_MUTATION, {
    refetchQueries: [SECTION_CONSULTANT_ASSIGNMENTS_QUERY, CONFIGURATION_SECTIONS_QUERY],
    awaitRefetchQueries: true
  });
  const [deleteAssignment, { loading: removing }] = useMutation(DELETE_SECTION_CONSULTANT_ASSIGNMENT_MUTATION, {
    refetchQueries: [SECTION_CONSULTANT_ASSIGNMENTS_QUERY, CONFIGURATION_SECTIONS_QUERY],
    awaitRefetchQueries: true
  });

  const loading = assignmentsLoading || sectionsLoading;
  const error = assignmentsError || (groupBy === "section" && sectionsError);
  const truncated =
    isCoverageListTruncated(assignmentsData?.sectionConsultantAssignments) ||
    (groupBy === "section" && isCoverageListTruncated(sectionsData?.crmSections));

  const groups = useMemo(() => {
    const assignmentNodes = assignmentsData?.sectionConsultantAssignments.nodes ?? [];
    const sectionNodes = sectionsData?.crmSections.nodes ?? [];
    const grouped =
      groupBy === "section"
        ? groupCareerAssignmentsBySection(sectionNodes, assignmentNodes)
        : groupCareerAssignmentsByConsultant(assignmentNodes, sectionNodes);
    return filterCoverageGroups(grouped, debouncedSearch);
  }, [assignmentsData, debouncedSearch, groupBy, sectionsData]);

  const openAssign = (target: { sectionId?: string; sectionName?: string; consultant?: CoverageStaff | null }) => {
    setAssignTarget(target);
  };

  const handleAddGroup = (group: CoverageGroupRow) => {
    openAssign({
      sectionId: group.lockedSectionId,
      sectionName: group.lockedSectionName,
      consultant: group.lockedPerson ?? null
    });
  };

  const handleAddMember = (member: CoverageGroupMember) => {
    openAssign({
      sectionId: member.sectionId,
      sectionName: member.sectionName
    });
  };

  const handleAssign = async ({ sectionId, backofficeUserId }: { sectionId: string; backofficeUserId: string }) => {
    try {
      const result = await upsertAssignment({
        variables: { input: { sectionId, backofficeUserId, isActive: true } }
      });
      const payload = result.data?.upsertSectionConsultantAssignment;
      if (payload?.success) {
        showSnackbar(payload.message);
        setAssignTarget(null);
        return;
      }
      setError(payload?.message || "Couldn't assign this Career Consultant.");
    } catch (caught) {
      setError(mutationErrorMessage(caught, "Couldn't assign this Career Consultant."));
    }
  };

  const handleRemove = async () => {
    if (!assignmentToRemove?.assignmentId) return;

    try {
      const result = await deleteAssignment({ variables: { id: assignmentToRemove.assignmentId } });
      const payload = result.data?.deleteSectionConsultantAssignment;
      if (payload?.success) {
        showSnackbar(payload.message);
        setAssignmentToRemove(null);
        return;
      }
      setError(payload?.message || "Couldn't remove this Career Consultant.");
    } catch (caught) {
      setError(mutationErrorMessage(caught, "Couldn't remove this Career Consultant."));
    }
  };

  return (
    <TabLayout>
      {!error ? <CoverageGroupTabs consultantTabLabel="By Career Consultant" onGroupByChange={clearSearch} /> : null}
      <Menu />
      <div className="pt-12" />
      {error ? (
        <ConfigurationLoadError
          onRetry={() => {
            refetchAssignments();
            refetchSections();
          }}
        />
      ) : (
        <GroupedCoverageTable
          groups={groups}
          loading={loading}
          truncated={truncated}
          groupBy={groupBy}
          groupColumnHeader={groupBy === "section" ? "Section" : "Career Consultant"}
          addActionLabel={groupBy === "section" ? "Add Consultant" : "Add Section"}
          memberAddActionLabel="Add Consultant"
          removeActionLabel="Remove"
          searchPlaceholder={groupBy === "section" ? "Search Sections" : "Search Career Consultants"}
          searchValue={searchValue}
          hasActiveFilters={!!debouncedSearch}
          onSearchChange={setSearchValue}
          onSearchClear={clearSearch}
          tableTestId="career-consultants-table"
          emptyTitle={groupBy === "section" ? "No sections found." : "No Career Consultant assignments yet."}
          emptyCaption={groupBy === "consultant" ? "Switch to By Section to add a consultant." : undefined}
          emptyIcon="group"
          setMenu={setMenu}
          onAdd={handleAddGroup}
          onAddMember={handleAddMember}
          onRemoveMember={setAssignmentToRemove}
        />
      )}
      <AssignCareerConsultantDialog
        open={Boolean(assignTarget)}
        onClose={() => setAssignTarget(null)}
        onAssign={handleAssign}
        saving={assigning}
        sectionId={assignTarget?.sectionId}
        sectionName={assignTarget?.sectionName}
        consultant={assignTarget?.consultant}
      />
      <ConfirmCoverageChangeDialog
        open={Boolean(assignmentToRemove)}
        onClose={() => setAssignmentToRemove(null)}
        onConfirm={handleRemove}
        saving={removing}
        headline="Remove Career Consultant"
        body={
          assignmentToRemove
            ? `Remove ${assignmentToRemove.person?.name ?? "this Career Consultant"} as a Career Consultant for ${assignmentToRemove.sectionName}?`
            : ""
        }
        confirmLabel="Remove"
      />
    </TabLayout>
  );
}
